import { FormEvent, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Close, Search } from '@mui/icons-material';
import Label from '../Label/Label';
import cn from 'utils/cn';
import { inputVariants, iconVariants, dividerVariants, type Variant } from './SearchInput.variants';
import { SlotProps } from './SearchInput.interface';

export type SearchInputProps = {
  animated?: boolean;
  label: string;
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  containerProps?: React.AllHTMLAttributes<HTMLFormElement>;
  onClear: () => void;
  onSubmit?: () => void;
  variant?: Variant;
  slotProps?: SlotProps;
};

const SearchInput = ({
  animated = false,
  slotProps = {},
  label,
  labelProps,
  inputProps,
  containerProps,
  onClear,
  onSubmit,
  variant = 'shade',
}: SearchInputProps): ReactElement<any> => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [active, setActive] = useState(false);
  const controlled = inputProps?.value !== undefined;
  const valuePresent = controlled ? !!inputProps?.value : !!inputRef.current?.value;

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (expanded && !valuePresent) {
        inputRef.current?.focus();
        return;
      }
      onSubmit?.();
    },
    [expanded, onSubmit, valuePresent]
  );

  const handleBlur = useCallback(() => {
    setActive(false);
    if (animated)
      setTimeout(() => {
        const present = controlled ? !!inputProps?.value : !!inputRef.current?.value;
        if (!present) setExpanded(false);
      }, 100);
  }, [animated, controlled, inputProps?.value]);

  const expand = useCallback(() => {
    setActive(true);
    if (animated && !expanded) {
      setExpanded(true);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [animated, expanded]);

  const handleClear = useCallback(() => {
    if (!controlled && inputRef.current) inputRef.current.value = '';
    onClear();
    inputRef.current?.focus();
  }, [controlled, onClear]);

  useEffect(() => {
    if (animated && controlled && !!inputProps?.value) setExpanded(true);
  }, [animated, controlled, inputProps?.value]);

  const fullWidth = useMemo(
    () =>
      typeof inputProps?.style?.width === 'number' ? (inputProps.style!.width as number) : 450,
    [inputProps?.style]
  );

  const wrapperStyle: React.CSSProperties = animated
    ? { width: expanded ? fullWidth : 40, transition: 'width 0.3s' }
    : { width: fullWidth };

  const widthClass = animated
    ? expanded
      ? 'tw:!w-full tw:opacity-100'
      : 'tw:!w-0 tw:opacity-0'
    : 'tw:!w-full';

  const borderClass = animated
    ? expanded
      ? 'tw:border-l tw:border-r-0 tw:border-primary-500 tw:border-t-0 tw:border-b-0 tw:rounded-none'
      : 'tw:border-0'
    : 'tw:border tw:border-primary-500';

  return (
    <form
      data-theme={animated ? 'dark' : 'light'}
      {...containerProps}
      onSubmit={handleSubmit}
      className={cn(containerProps?.className)}
    >
      {label && (
        <Label
          labelProps={{ htmlFor: inputProps?.id || 'search-input', ...labelProps }}
          label={label}
        />
      )}
      <div className="tw:relative" style={wrapperStyle}>
        <input
          {...inputProps}
          ref={inputRef}
          type="text"
          id={inputProps?.id || 'search-input'}
          data-testid={inputProps?.id || 'search-input'}
          onFocus={expand}
          onBlur={handleBlur}
          className={cn(
            inputVariants({ variant: slotProps.input?.variant || variant }),
            widthClass,
            borderClass,
            inputProps?.className
          )}
          style={{ ...inputProps?.style, ...slotProps.input?.style }}
          placeholder={inputProps?.placeholder || 'Search'}
        />
        <button
          data-testid="clear-input"
          type="button"
          className={
            valuePresent
              ? 'tw:cursor-pointer tw:border-0 tw:absolute tw:top-[1px] tw:right-[42px] tw:w-10 tw:h-[38px] bg-transparent'
              : 'tw:hidden'
          }
          onClick={handleClear}
        >
          <Close
            className={cn(
              iconVariants({ variant: slotProps.icon?.variant || variant, size: 'sm' }),
              slotProps.icon?.className
            )}
          />
        </button>

        <div
          className={cn(
            dividerVariants({ variant: slotProps.divider?.variant || variant }),
            animated && 'tw:h-10 tw:top-0',
            animated && active && 'tw:bg-primary-600 tw:h-6 tw:top-2',
            slotProps.divider?.className
          )}
        />
        <button
          className="tw:cursor-pointer tw:border-0 tw:absolute tw:top-[4px] tw:right-[1px] tw:w-10 tw:h-[38px] tw:bg-transparent"
          data-testid="search-input"
          type="submit"
          onClick={expand}
        >
          <Search
            className={cn(
              iconVariants({ variant: slotProps.icon?.variant || variant }),
              slotProps.icon?.className
            )}
          />
        </button>
      </div>
    </form>
  );
};

export default SearchInput;
